import React, { useCallback } from 'react'
import { useNavigate } from 'react-router'
import { useAddToBookmarks } from '../../../components/add-to-bookmarks'
import { useDisplayAddToPlaylist } from '../../../hooks/useDisplayAddToPlaylist'
import { useCardExpand } from '../../../components/general-card/hooks'
import { AddToPlaylistButton, BookmarkButton, LikeButton } from '../../../components/action-buttons'
import { BaseContentCard } from '../../../components/general-card'
import { Bookmark, CollectionsBookmark } from '@mui/icons-material'
import { useLike } from '../../../components/like'
import { Box } from '@mui/material'
import { makeStyles } from '@mui/styles'
import { useHomeEnterpriseContext } from '../context/home-enterprise-page-context'
import { useTrackHomeEnterpriseEvent } from '../hooks/useTrackHomeEnterpriseEvent'

export const HomeContentCard = ({
  content,
  dataTest,
  railType,
  itemIndex,
  extraTrackingData = {}
}) => {
  const navigate = useNavigate()
  const { showAddToPlaylist } = useHomeEnterpriseContext()
  const { isBookmarked, handleBookmark, bookmarking } = useAddToBookmarks({ from: 'home-enterprise' })
  const { isLiked, handleLike, liking } = useLike({ from: 'home-enterprise' })
  const { trackArticleClickEvent } = useTrackHomeEnterpriseEvent()

  const onSelect = useCallback(() => {
    if (!content) return
    trackArticleClickEvent(railType, content.locationId, {
      title: content.title,
      itemIndex,
      ...extraTrackingData
    })
    navigate(`/${content.locationId}/${content.staticUrl}?from=home-enterprise`)
  }, [railType, navigate, content, trackArticleClickEvent, itemIndex, extraTrackingData])

  const onBookmark = useCallback(() => {
    if (!content) return
    handleBookmark(content)
  }, [handleBookmark, content])

  return (
    <ContentCard
      content={content}
      onSelect={onSelect}
      onBookmark={onBookmark}
      bookmarked={isBookmarked(content)}
      isBookmarking={bookmarking(content)}
      isLiking={liking(content || {})}
      onLike={() => handleLike(content)}
      isLiked={isLiked(content || {})}
      onAddToPlaylist={() => showAddToPlaylist(content)}
      testId={dataTest}
    />
  )
}

const useStyles = makeStyles(() => ({
  actions: {
    marginTop: '12px',
    marginBottom: '12px'
  },
  summary: {
    padding: '8px',
    height: '206px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between'
  },
  subtitle: {
    lineClamp: 2,
    WebkitLineClamp: 2
  },
  title: {
    minHeight: 'auto'
  },
  root: {
    height: '300px'
  },
  card: {
    height: '100%'
  }
}))

/** Card for displaying content such as articles, videos and audio etc */
const ContentCard = ({
  expand,
  onAddToPlaylist,
  onBookmark,
  bookmarked,
  isBookmarking,
  isLiking,
  onLike,
  isLiked,
  ...props
}) => {
  const displayAddToPlaylist = useDisplayAddToPlaylist()
  const classes = useStyles()

  const {
    expanded,
    handleMouseEnter,
    handleMouseLeave
  } = useCardExpand({ expand })

  const actions = {
    leftActions: (
      <>
        <Box display='flex' flexDirection='row' gap={1} justifyContent='flex-start' alignItems='center'>
          <LikeButton
            size='xs'
            label=''
            hasLiked={isLiked}
            loading={isLiking}
            hideText
            onClick={onLike}
          />
          <BookmarkButton
            size='xs'
            variant={bookmarked ? 'secondary' : 'secondary-noselect'}
            onClick={() => {
              onBookmark()
            }}
            loading={isBookmarking}
            hideText
            startIcon={<Bookmark />}
            bookmarked={bookmarked}
          />
          {displayAddToPlaylist && (
            <AddToPlaylistButton
              size='xs'
              variant='secondary-noselect'
              startIcon={<CollectionsBookmark />}
              onClick={() => {
                onAddToPlaylist(props.content)
              }}
            />
          )}
        </Box>
      </>
    )
  }
  return (
    <BaseContentCard
      actions={actions}
      expand
      hoverShadow={expanded}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      overrides={{
        actions: classes.actions,
        summary: classes.summary,
        title: classes.title,
        subtitle: classes.subtitle,
        root: classes.root,
        card: classes.card
      }}
      {...props}
    />
  )
}

ContentCard.defaultProps = {
  size: 'small',
  expand: false
}
export default ContentCard
